import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  MessageSquare, Users, AlertTriangle, Clock, CheckCircle, 
  Zap, CreditCard, ArrowRight, RefreshCw, CalendarCheck
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { useAuth, API } from '../../App';
import ChannelSelector from '../../components/ChannelSelector';
import SetupStatusBanner from '../../components/dashboard/SetupStatusBanner';
import DemoModeToggleCard from '../../components/DemoModeToggleCard';
import { useDemoMode } from '../../contexts/DemoModeContext';
import axios from 'axios';
import { toast } from 'sonner';

const DashboardPage = () => {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const demoMode = useDemoMode();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [recentConversations, setRecentConversations] = useState([]);
  const [selectedChannel, setSelectedChannel] = useState(null);
  const [readiness, setReadiness] = useState(null);
  const [bannerDismissed, setBannerDismissed] = useState(false);

  const isOwner = user?.role === 'owner' || user?.is_super_admin;
  const canSeeReadiness = user?.role !== 'operator';

  useEffect(() => {
    fetchDashboardStats();
    fetchRecentConversations();
    if (canSeeReadiness) {
      fetchReadinessStatus();
    }
  }, [selectedChannel]);

  const fetchReadinessStatus = async () => {
    try {
      const response = await axios.get(`${API}/org/readiness-status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReadiness(response.data);
    } catch (error) {
      console.error('Failed to fetch readiness status:', error);
      // Non-critical - don't show error toast
    }
  };

  const fetchDashboardStats = async () => {
    try {
      const url = selectedChannel 
        ? `${API}/dashboard/stats?channel_id=${selectedChannel}`
        : `${API}/dashboard/stats`;
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch dashboard stats:', error);
      toast.error('Failed to load dashboard statistics');
    } finally {
      setLoading(false);
    }
  };

  const fetchRecentConversations = async () => {
    try {
      const url = selectedChannel
        ? `${API}/conversations?limit=5&channel_id=${selectedChannel}`
        : `${API}/conversations?limit=5`;
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRecentConversations(response.data.slice(0, 5));
    } catch (error) {
      console.error('Failed to fetch recent conversations:', error);
    }
  };

  const handleFilterClick = (filter) => {
    const channelParam = selectedChannel ? `&channel_id=${selectedChannel}` : '';
    navigate(`/dashboard/conversations?filter=${filter}${channelParam}`);
  };

  // Get dynamic color based on warning level
  const getWarningColor = (level) => {
    switch (level) {
      case 'critical': return 'bg-red-500';
      case 'warning': return 'bg-amber-500';
      default: return 'bg-purple-500';
    }
  };

  const getMessagesWarningColor = (level) => {
    switch (level) {
      case 'critical': return 'bg-red-500';
      case 'warning': return 'bg-amber-500';
      default: return 'bg-blue-600';
    }
  };

  const StatCard = ({ title, value, icon: Icon, color, onClick, badge, warningLevel }) => {
    // Use dynamic color if warningLevel is provided
    const cardColor = warningLevel ? 
      (title.includes('Credit') ? getWarningColor(warningLevel) : getMessagesWarningColor(warningLevel)) 
      : color;
    
    return (
      <Card 
        className={`cursor-pointer transition-all hover:shadow-lg ${onClick ? 'hover:scale-[1.02]' : ''}`}
        onClick={onClick}
        data-testid={`stat-card-${title.toLowerCase().replace(/\s+/g, '-')}`}
      >
        <CardContent className="pt-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">{title}</p>
              <p className="text-3xl font-bold mt-1">{value ?? '-'}</p>
              {badge && (
                <Badge variant="secondary" className="mt-2">{badge}</Badge>
              )}
            </div>
            <div className={`p-3 rounded-full ${cardColor}`}>
              <Icon className="w-6 h-6 text-white" />
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const convStats = stats?.conversations || {};
  const creditStats = stats?.credits || {};

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      {/* Setup Status Banner - Owner/Admin only. Suppressed in Demo Mode (demo banner takes over). */}
      {canSeeReadiness && !bannerDismissed && !demoMode.active && (
        <SetupStatusBanner 
          readiness={readiness}
          onDismiss={() => setBannerDismissed(true)}
          currentUserRole={user?.role}
        />
      )}

      {/* Demo Mode toggle — primary CTA (hidden for operators inside component) */}
      <DemoModeToggleCard variant="card" />

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Dashboard</h1>
          <p className="text-muted-foreground">Overview of your WhatsApp business</p>
        </div>
        <div className="flex items-center gap-3">
          {isOwner && (
            <ChannelSelector 
              value={selectedChannel}
              onChange={setSelectedChannel}
              showAllOption={true}
            />
          )}
          <Button variant="outline" onClick={fetchDashboardStats}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Channel Filter Indicator */}
      {stats?.filter_channel_id && (
        <div className="text-sm text-gray-500 flex items-center gap-2">
          <MessageSquare className="h-4 w-4" />
          Showing data for selected channel
        </div>
      )}

      {/* Conversation Status Cards - Clickable Filters */}
      <div>
        <h2 className="text-lg font-semibold mb-4">Conversation Status</h2>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          <StatCard
            title="Escalated"
            value={convStats.escalated || 0}
            icon={AlertTriangle}
            color="bg-orange-500"
            onClick={() => handleFilterClick('escalated')}
          />
          <StatCard
            title="Pending Human"
            value={convStats.pending_human || 0}
            icon={Clock}
            color="bg-amber-500"
            onClick={() => handleFilterClick('pending_human')}
          />
          <StatCard
            title="New Leads"
            value={convStats.new_leads || 0}
            icon={Users}
            color="bg-blue-500"
            onClick={() => handleFilterClick('new')}
          />
          <StatCard
            title="Collecting Info"
            value={convStats.collecting_info || 0}
            icon={MessageSquare}
            color="bg-emerald-500"
            onClick={() => handleFilterClick('collecting_info')}
          />
          <StatCard
            title="Resolved"
            value={convStats.resolved || 0}
            icon={CheckCircle}
            color="bg-green-600"
            onClick={() => handleFilterClick('resolved')}
          />
          <StatCard
            title="Closed"
            value={convStats.closed || 0}
            icon={CheckCircle}
            color="bg-gray-500"
            onClick={() => handleFilterClick('closed')}
          />
          <StatCard
            title="Booked / Converted"
            value={stats?.booked_conversations_count ?? 0}
            icon={CalendarCheck}
            color="bg-teal-600"
            onClick={() => handleFilterClick('booked')}
            data-testid="stat-card-booked-converted"
          />
        </div>
      </div>

      {/* Usage Stats */}
      <div>
        <h2 className="text-lg font-semibold mb-4">Usage & Credits</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <StatCard
            title="AI Credits"
            value={creditStats.credits_remaining ?? creditStats.ai_credits_remaining ?? 0}
            icon={Zap}
            color="bg-purple-500"
            badge={`${creditStats.credits_used ?? creditStats.ai_credits_used_total ?? 0} used`}
            warningLevel={creditStats.credits_warning_level}
          />
          <StatCard
            title="AI Replies Sent"
            value={creditStats.ai_replies_this_month ?? creditStats.messages_used ?? 0}
            icon={MessageSquare}
            color="bg-blue-600"
            badge={`${creditStats.messages_remaining ?? 0} remaining`}
            warningLevel={creditStats.messages_warning_level}
          />
          <StatCard
            title="Total Conversations"
            value={convStats.total || 0}
            icon={Users}
            color="bg-teal-500"
          />
        </div>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Escalated Conversations Alert */}
        {convStats.escalated > 0 && (
          <Card className="border-orange-500/50 bg-orange-500/5">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-orange-600">
                <AlertTriangle className="w-5 h-5" />
                Attention Required
              </CardTitle>
              <CardDescription>
                {convStats.escalated} conversation{convStats.escalated > 1 ? 's' : ''} need{convStats.escalated === 1 ? 's' : ''} human review
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button 
                onClick={() => handleFilterClick('escalated')}
                className="bg-orange-600 hover:bg-orange-700"
              >
                View Escalated
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Recent Conversations */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MessageSquare className="w-5 h-5" />
              Recent Activity
            </CardTitle>
            <CardDescription>Latest conversations</CardDescription>
          </CardHeader>
          <CardContent>
            {recentConversations.length > 0 ? (
              <div className="space-y-3">
                {recentConversations.map((conv) => (
                  <div 
                    key={conv.id}
                    className="flex items-center justify-between p-2 rounded-lg hover:bg-muted cursor-pointer"
                    onClick={() => navigate(`/dashboard/conversations?selected=${conv.id}`)}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center">
                        <Users className="w-4 h-4 text-emerald-600" />
                      </div>
                      <div>
                        <p className="text-sm font-medium">{conv.customer_name || conv.customer_phone}</p>
                        <p className="text-xs text-muted-foreground">
                          {conv.conversation_status || conv.status}
                        </p>
                      </div>
                    </div>
                    {conv.escalation_required && (
                      <Badge variant="destructive" className="text-xs">Escalated</Badge>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">No recent conversations</p>
            )}
            <Button 
              variant="outline" 
              className="w-full mt-4"
              onClick={() => navigate('/dashboard/conversations')}
            >
              View All Conversations
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default DashboardPage;
